import {DOCUMENT} from '@angular/common';
import {Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, Renderer2, inject} from '@angular/core';

const TOOLTIP_EDGE_MARGIN = 8;
const TOOLTIP_GAP = 8;
let nextTooltipId = 0;

@Directive({selector: '[smPlainTextTooltip]', standalone: true})
export class SmPlainTextTooltipDirective implements OnChanges, OnDestroy {
  @Input() smPlainTextTooltip: string | null = null;
  @Input() smPlainTextTooltipAnchor: string | null = null;
  @Input() smPlainTextTooltipBlurOnEscape = true;
  @Input() smPlainTextTooltipDisabled = false;

  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly trigger: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly tooltipId = `sm-plain-text-tooltip-${++nextTooltipId}`;
  private panel: HTMLElement | null = null;
  private panelHost: HTMLElement | null = null;
  private positionTimer: number | null = null;
  private triggerHovered = false;
  private triggerFocused = false;

  ngOnChanges(): void {
    if (!this.smPlainTextTooltip || this.smPlainTextTooltipDisabled) {
      this.hideTooltip();
      return;
    }

    if (this.panel) {
      this.renderer.setProperty(this.panel, 'textContent', this.smPlainTextTooltip);
      this.schedulePosition();
    }
  }

  @HostListener('mouseenter')
  handleMouseEnter(): void {
    this.triggerHovered = true;
    this.showTooltip();
  }

  @HostListener('mouseleave')
  handleMouseLeave(): void {
    this.triggerHovered = false;
    this.hideTooltipIfInactive();
  }

  @HostListener('focus')
  handleFocus(): void {
    this.triggerFocused = true;
    this.showTooltip();
  }

  @HostListener('blur')
  handleBlur(): void {
    this.triggerFocused = false;
    this.hideTooltipIfInactive();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  hideTooltip(): void {
    this.cancelPositionTimer();
    if (this.panel?.matches(':popover-open')) this.panel.hidePopover();
    if (this.panel && this.panelHost && this.panel.parentElement === this.panelHost) {
      this.renderer.removeChild(this.panelHost, this.panel);
    }
    this.panel = null;
    this.panelHost = null;
    this.removeDescription();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (!this.panel) return;
    this.triggerHovered = false;
    this.triggerFocused = false;
    if (this.smPlainTextTooltipBlurOnEscape) this.trigger.blur();
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (!this.smPlainTextTooltip || this.smPlainTextTooltipDisabled || this.panel) return;

    const panel = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(panel, 'sm-plain-text-tooltip');
    this.renderer.setAttribute(panel, 'id', this.tooltipId);
    this.renderer.setAttribute(panel, 'role', 'tooltip');
    this.renderer.setProperty(panel, 'textContent', this.smPlainTextTooltip);
    this.renderer.setStyle(panel, 'left', '-9999px');
    this.renderer.setStyle(panel, 'top', '-9999px');
    const nativeHost = this.trigger.closest<HTMLElement>('dialog[data-sm-modal][open], [popover]:popover-open');
    const host = nativeHost ?? this.document.body;
    if (nativeHost) this.renderer.setAttribute(panel, 'popover', 'manual');
    this.renderer.appendChild(host, panel);
    if (nativeHost) panel.showPopover();
    this.panel = panel;
    this.panelHost = host;
    this.addDescription();
    this.schedulePosition();
  }

  private hideTooltipIfInactive(): void {
    if (!this.triggerHovered && !this.triggerFocused) this.hideTooltip();
  }

  private schedulePosition(): void {
    this.cancelPositionTimer();
    this.positionTimer = window.setTimeout(() => {
      this.positionTimer = null;
      this.positionTooltip();
    }, 0);
  }

  private positionTooltip(): void {
    const panel = this.panel;
    if (!panel) return;

    const anchor = this.smPlainTextTooltipAnchor
      ? this.trigger.querySelector<HTMLElement>(this.smPlainTextTooltipAnchor) ?? this.trigger
      : this.trigger;
    const triggerRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const maxLeft = Math.max(TOOLTIP_EDGE_MARGIN, window.innerWidth - panelRect.width - TOOLTIP_EDGE_MARGIN);
    const centeredLeft = triggerRect.left + (triggerRect.width - panelRect.width) / 2;
    const left = Math.min(Math.max(centeredLeft, TOOLTIP_EDGE_MARGIN), maxLeft);
    const preferredTop = triggerRect.top - panelRect.height - TOOLTIP_GAP;
    const top = preferredTop >= TOOLTIP_EDGE_MARGIN
      ? preferredTop
      : Math.min(triggerRect.bottom + TOOLTIP_GAP, window.innerHeight - panelRect.height - TOOLTIP_EDGE_MARGIN);

    this.renderer.setStyle(panel, 'left', `${left}px`);
    this.renderer.setStyle(panel, 'top', `${Math.max(TOOLTIP_EDGE_MARGIN, top)}px`);
    this.renderer.addClass(panel, 'sm-plain-text-tooltip--ready');
  }

  private addDescription(): void {
    const descriptions: string[] = this.trigger.getAttribute('aria-describedby')?.split(/\s+/).filter(Boolean) ?? [];
    this.renderer.setAttribute(this.trigger, 'aria-describedby', [...descriptions, this.tooltipId].join(' '));
  }

  private removeDescription(): void {
    const descriptions: string[] = this.trigger.getAttribute('aria-describedby')?.split(/\s+/).filter(Boolean) ?? [];
    const remaining = descriptions.filter(id => id !== this.tooltipId);
    if (remaining.length) this.renderer.setAttribute(this.trigger, 'aria-describedby', remaining.join(' '));
    else this.renderer.removeAttribute(this.trigger, 'aria-describedby');
  }

  private cancelPositionTimer(): void {
    if (this.positionTimer === null) return;
    window.clearTimeout(this.positionTimer);
    this.positionTimer = null;
  }
}
